import { useState, useEffect, useCallback } from 'react'
import { useNotification } from '../../../contexts/NotificationContext'
import { useLeadStatuses, LeadStatusesResponse } from '../../../hooks/leads/useLeadStatuses'
import { StatusItem } from './EditStatusList'
import { extractErrorMessage } from '../../../utils/errorUtils'

interface UseAddCustomLabelProps {
  open: boolean
  smbId: string
  customStatuses: string[]
  onClose: () => void
  onComplete: (res?: LeadStatusesResponse, createdStatus?: string) => void
}

export const useAddCustomLabel = ({
  open,
  smbId,
  customStatuses,
  onClose,
  onComplete,
}: UseAddCustomLabelProps) => {
  const [newLabelInput, setNewLabelInput] = useState('')
  const [editingStatus, setEditingStatus] = useState<string | null>(null)
  const [renameInput, setRenameInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState(0)
  const [displayList, setDisplayList] = useState<StatusItem[]>([])
  const { showNotification } = useNotification()

  const {
    fetchStatuses,
    addStatus,
    renameStatus,
    defaultStatuses,
    customStatuses: customOnly,
    loading: hookLoading,
  } = useLeadStatuses(smbId)

  useEffect(() => {
    if (open && activeTab === 1) {
      if (smbId) {
        fetchStatuses().then((res) => {
          const fetchedDefaultStatuses = res?.default || defaultStatuses || []
          const fetchedCustomStatuses =
            res?.custom ||
            (customOnly.length > 0
              ? customOnly
              : (customStatuses || []).filter((s) => !fetchedDefaultStatuses.includes(s)))
          const initialList: StatusItem[] = [
            ...fetchedCustomStatuses.map((s: string) => ({ name: s, isDefault: false })),
            ...fetchedDefaultStatuses.map((s: string) => ({ name: s, isDefault: true })),
          ].sort((a, b) => {
            if (a.isDefault === b.isDefault) return a.name.localeCompare(b.name)
            return a.isDefault ? 1 : -1
          })
          setDisplayList(initialList)
        })
      } else {
        const filteredCustomStatuses = (customStatuses || []).filter((s) => !defaultStatuses.includes(s))
        const initialList: StatusItem[] = [
          ...filteredCustomStatuses.map((s: string) => ({ name: s, isDefault: false })),
          ...defaultStatuses.map((s: string) => ({ name: s, isDefault: true })),
        ].sort((a, b) => {
          if (a.isDefault === b.isDefault) return a.name.localeCompare(b.name)
          return a.isDefault ? 1 : -1
        })
        setDisplayList(initialList)
      }
    } else {
      setDisplayList([])
    }
  }, [open, activeTab, smbId, fetchStatuses])

  const handleClose = useCallback(() => {
    if (!isLoading && !hookLoading) {
      setNewLabelInput('')
      setEditingStatus(null)
      setRenameInput('')
      setError(null)
      setActiveTab(0)
      setDisplayList([])
      onClose()
    }
  }, [isLoading, hookLoading, onClose])

  const handleCreateSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const collapsed = newLabelInput.trim()
      if (!collapsed) return

      setIsLoading(true)
      setError(null)
      try {
        if (smbId) {
          const updatedRes = await addStatus(collapsed)
          showNotification('New status created successfully', 'success')
          await onComplete(updatedRes, collapsed)
          setNewLabelInput('')
          onClose()
        }
      } catch (err: any) {
        console.error(err)
        setError(extractErrorMessage(err, 'Failed to create custom status'))
      } finally {
        setIsLoading(false)
      }
    },
    [newLabelInput, smbId, addStatus, showNotification, onComplete, onClose]
  )

  const handleRenameInitiate = useCallback(
    async (oldStatus: string) => {
      const collapsed = renameInput.trim()
      if (!collapsed || collapsed === oldStatus) {
        setEditingStatus(null)
        return
      }

      setIsLoading(true)
      setError(null)
      try {
        if (smbId) {
          const updatedRes = await renameStatus(oldStatus, collapsed)
          setDisplayList((prev) =>
            prev.map((item) =>
              item.name === oldStatus ? { ...item, name: collapsed } : item
            )
          )
          showNotification('Status renamed and leads updated successfully', 'success')
          await onComplete(updatedRes)
          setEditingStatus(null)
        }
      } catch (err: any) {
        console.error(err)
        setError(extractErrorMessage(err, 'Failed to rename status'))
      } finally {
        setIsLoading(false)
      }
    },
    [renameInput, smbId, renameStatus, showNotification, onComplete]
  )

  return {
    newLabelInput,
    setNewLabelInput,
    editingStatus,
    setEditingStatus,
    renameInput,
    setRenameInput,
    isLoading,
    error,
    setError,
    activeTab,
    setActiveTab,
    displayList,
    handleClose,
    handleCreateSubmit,
    handleRenameInitiate,
  }
}
